// Single source of truth for "is this profile complete enough to contact an
// owner". Two consumers with different semantics:
//
//   * getMissingContactFields, the CONTACT GATE. Per-tenant configurable
//     (admin Quality control → "Renter contact requirements"): the caller
//     passes the tenant's required field set and gets back which of those the
//     user still needs to fill. Drives the quick-complete dialog on
//     PropertyDetail and in the concierge.
//   * meetsProfileCompleted, the global `profiles.profile_completed` RATCHET
//     rule. That flag is load-bearing beyond contacting (matchable-profiles
//     RLS policy, owner dashboards, listing-alert emails), so it is only ever
//     auto-PROMOTED when the rule is met, never auto-demoted (admin can
//     demote manually).

/** The fields a tenant can require before a renter may contact an owner. */
export type ContactFieldKey = "name" | "phone" | "introduction";

export const ALL_CONTACT_FIELDS: ContactFieldKey[] = ["name", "phone", "introduction"];

/** Per-tenant requirement toggles (quality_config.renter_contact_fields). */
export type ContactRequirements = Record<ContactFieldKey, boolean>;

/** Matches the DB column default, used as the client fallback when the RPC
 *  is unavailable, so the gate fails closed to the standard bar. */
export const DEFAULT_CONTACT_REQUIREMENTS: ContactRequirements = {
  name: true,
  phone: true,
  introduction: true,
};

export interface ContactFieldValues {
  displayName?: string | null;
  phone?: string | null;
  introduction?: string | null;
}

const filled = (v: string | null | undefined): boolean => !!v && v.trim().length > 0;

/** Coerce a raw jsonb payload (RPC / quality_config column) into a full
 *  ContactRequirements object; unknown/missing keys fall back to the default. */
export const normalizeContactRequirements = (raw: unknown): ContactRequirements => {
  const obj = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const out = { ...DEFAULT_CONTACT_REQUIREMENTS };
  for (const key of ALL_CONTACT_FIELDS) {
    if (typeof obj[key] === "boolean") out[key] = obj[key] as boolean;
  }
  return out;
};

/** The required-by-config fields the user hasn't filled yet (trimmed). */
export const getMissingContactFields = (
  values: ContactFieldValues,
  required: ContactRequirements = DEFAULT_CONTACT_REQUIREMENTS,
): ContactFieldKey[] => {
  const missing: ContactFieldKey[] = [];
  if (required.name && !filled(values.displayName)) missing.push("name");
  if (required.phone && !filled(values.phone)) missing.push("phone");
  if (required.introduction && !filled(values.introduction)) missing.push("introduction");
  return missing;
};

/**
 * Global ratchet rule for `profiles.profile_completed` (NOT tenant-scoped):
 * renters need name + phone + introduction; owners and role-less users need
 * name + phone (the exact set OwnerOnboarding already validates).
 */
export const meetsProfileCompleted = (
  userTypes: string[] | null | undefined,
  values: ContactFieldValues,
): boolean => {
  const base = filled(values.displayName) && filled(values.phone);
  if (!base) return false;
  const isRenter = (userTypes ?? []).includes("renter");
  return isRenter ? filled(values.introduction) : true;
};
